import { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    Container,
    TextField,
    Typography,
    Paper,
    Alert,
    CircularProgress,
    InputAdornment,
    IconButton
} from '@mui/material';
import { CheckCircle, Visibility, VisibilityOff } from '@mui/icons-material';
import { FddApi } from '../../ui/services/server-fdd';
import logo from '../../assets/webp/logo_complete.webp';
import logoWhite from '../../assets/webp/logo_complete_white.webp';
import { useThemeMode } from '../../ui/context/theme/ThemeProvider';

export const ResetPasswordPage = () => {
    const { mode } = useThemeMode();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    const [formData, setFormData] = useState({ newPassword: '', confirmPassword: '' });
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    // Token ausente en la URL
    if (!token) {
        return (
            <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', bgcolor: 'background.default' }}>
                <Container maxWidth="xs">
                    <Paper elevation={3} sx={{ p: 6, borderRadius: 2, textAlign: 'center' }}>
                        <Alert severity="error" sx={{ mb: 3 }}>
                            Enlace inválido o expirado. Solicita uno nuevo.
                        </Alert>
                        <Button component={Link} to="/forgot-password" variant="contained" fullWidth>
                            Solicitar nuevo enlace
                        </Button>
                    </Paper>
                </Container>
            </Box>
        );
    }

    const handleChange = (e) => {
        setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
        setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (formData.newPassword.length < 8) {
            setError('La contraseña debe tener al menos 8 caracteres');
            return;
        }
        if (formData.newPassword !== formData.confirmPassword) {
            setError('Las contraseñas no coinciden');
            return;
        }

        setLoading(true);
        try {
            const result = await FddApi.resetPassword(token, formData.newPassword);
            if (result.hasError) {
                setError(result.message || 'Error al restablecer la contraseña');
            } else {
                setSuccess(true);
                setTimeout(() => navigate('/login'), 3000);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', bgcolor: 'background.default' }}>
            <Container maxWidth="xs">
                <Paper elevation={3} sx={{ p: { xs: 5, sm: 6 }, borderRadius: 2, mx: 'auto', maxWidth: 420 }}>
                    <Box
                        component="img"
                        src={mode === 'dark' ? logoWhite : logo}
                        alt="Neftis SecureSign"
                        sx={{ width: '70%', maxWidth: 280, mb: 4, display: 'block', mx: 'auto' }}
                    />

                    {success ? (
                        <Box sx={{ textAlign: 'center' }}>
                            <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
                            <Typography variant="h5" fontWeight="600" gutterBottom>
                                ¡Contraseña actualizada!
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                Tu contraseña ha sido restablecida correctamente. Serás redirigido al inicio de sesión en unos segundos.
                            </Typography>
                            <Button component={Link} to="/login" variant="contained" fullWidth>
                                Ir al inicio de sesión
                            </Button>
                        </Box>
                    ) : (
                        <>
                            <Typography component="h1" variant="h5" fontWeight="600" gutterBottom>
                                Nueva contraseña
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                Ingresa y confirma tu nueva contraseña.
                            </Typography>

                            {error && (
                                <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
                            )}

                            <Box component="form" onSubmit={handleSubmit}>
                                <TextField
                                    fullWidth
                                    margin="normal"
                                    name="newPassword"
                                    label="Nueva contraseña"
                                    type={showPassword ? 'text' : 'password'}
                                    value={formData.newPassword}
                                    onChange={handleChange}
                                    disabled={loading}
                                    helperText="Mínimo 8 caracteres"
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton onClick={() => setShowPassword(v => !v)} edge="end">
                                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                />
                                <TextField
                                    fullWidth
                                    margin="normal"
                                    name="confirmPassword"
                                    label="Confirmar nueva contraseña"
                                    type={showPassword ? 'text' : 'password'}
                                    value={formData.confirmPassword}
                                    onChange={handleChange}
                                    disabled={loading}
                                    error={!!formData.confirmPassword && formData.newPassword !== formData.confirmPassword}
                                    helperText={formData.confirmPassword && formData.newPassword !== formData.confirmPassword ? 'Las contraseñas no coinciden' : ''}
                                />
                                <Button
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    size="large"
                                    sx={{ mt: 2, mb: 2, py: 1.2 }}
                                    disabled={loading}
                                >
                                    {loading ? <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} /> : null}
                                    {loading ? 'Guardando...' : 'Establecer nueva contraseña'}
                                </Button>
                            </Box>
                        </>
                    )}
                </Paper>
            </Container>
        </Box>
    );
};

export default ResetPasswordPage;
